import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, ExternalLinkIcon, ShieldCheckIcon } from 'lucide-react';
import { StepShell } from './StepShell';
import { ChoiceButton } from './ChoiceButton';
import { MoneyInput } from './MoneyInput';
import { NumberStepper } from './NumberStepper';
import { DebtsGroup } from './DebtsGroup';
import { RevealScreen } from './RevealScreen';
import { EstimateButton } from './EstimateButton';
import { MinimumDepositNote } from './MinimumDepositNote';
import { StampDutyPanel } from './StampDutyPanel';
import { BROKER_REASSURANCE, CALENDLY_URL, COMMITMENTS_NOTE, EMPLOYMENT_OPTIONS, INCOME_EXPLAINER, PAY_FREQUENCY_OPTIONS, PRIVACY_NOTE, SAVINGS_GOAL_EXPLAINER, TIMEFRAME_OPTIONS } from '../../data/roadmapContent';
import type { Stage } from '../../data/roadmapContent';
import { estimateCashBuffer, estimatePurchasingCosts } from '../../utils/roadmapCalcs';
import type { Applicant, Debts, RoadmapAnswers, RoadmapResults } from '../../types/roadmap';
interface FlowProps {
  answers: RoadmapAnswers;
  results: RoadmapResults;
  set: <K extends keyof RoadmapAnswers>(key: K, value: RoadmapAnswers[K]) => void;
  setApplicant: (which: 'applicantOne' | 'applicantTwo', patch: Partial<Applicant>) => void;
  setDebts: (patch: Partial<Debts>) => void;
  /** Called when the last question is answered. */
  onFinish: () => void;
  /** Called when Back is pressed on the very first question. */
  onExit: () => void;
}

/** Progress details only. Navigation is taken from the enclosing scope, so a
 *  screen can never accidentally inherit a Continue button it shouldn't have. */
interface StepMeta {
  step: number;
  total: number;
  stage: Stage;
}
interface StepDef {
  stage: Stage;
  render: (meta: StepMeta) => React.ReactNode;
}
function BookCallLink({
  label = 'Book a Call With Liam'
}: {
  label?: string;
}) {
  return <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white text-lg font-semibold transition-[transform,box-shadow] duration-200 ease-out hover:shadow-[0_0_30px_rgba(20,184,166,0.35)] hover:scale-[1.01]" data-id="element-265" data-mp="11bwne3d0euql">
      {label}
      <ExternalLinkIcon className="w-5 h-5" data-id="element-266" data-mp="1n3h7gzpumtdx" />
    </a>;
}
export function RoadmapFlow({
  answers,
  results,
  set,
  setApplicant,
  setDebts,
  onFinish,
  onExit
}: FlowProps) {
  const [index, setIndex] = useState(0);

  // Each screen is a fresh question, so it starts at the top of itself rather
  // than inheriting the previous screen's scroll position.
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, [index]);
  const isInvestor = answers.goal === 'first-investment';
  const isJoint = answers.buyingWith === 'together';
  const steps: StepDef[] = [];

  // Declared before the step list so every screen closes over the same
  // navigation. `stepCount` is only read at click time, by which point the
  // current render has finished building the list.
  let stepCount = 0;
  const goNext = () => {
    setIndex(current => {
      if (current + 1 >= stepCount) {
        onFinish();
        return current;
      }
      return current + 1;
    });
  };
  const goBack = () => {
    setIndex(current => {
      if (current === 0) {
        onExit();
        return 0;
      }
      return current - 1;
    });
  };

  /** Choice answers advance themselves — one tap, no Continue to reach. */
  const pick = <K extends keyof RoadmapAnswers,>(key: K, value: RoadmapAnswers[K]) => {
    set(key, value);
    goNext();
  };

  // ---- Your goal ----------------------------------------------------------

  steps.push({
    stage: 'Your goal',
    render: meta => <StepShell {...meta} question="What are you saving for?" note="This shapes everything else we work out together." onBack={goBack} data-id="element-267" data-mp="1niw3tsxkraz0">
        <div className="space-y-3" data-id="element-268" data-mp="36exuiv4j6zjj">
          <ChoiceButton label="My first home" description="A place for you to live in." selected={answers.goal === 'first-home'} onClick={() => pick('goal', 'first-home')} data-id="element-269" data-mp="36eo5b9rb8wew" />
          <ChoiceButton label="My first investment property" description="A property to rent out." selected={answers.goal === 'first-investment'} onClick={() => pick('goal', 'first-investment')} data-id="element-270" data-mp="2xcyb5w61njdf" />
        </div>
      </StepShell>
  });
  steps.push({
    stage: 'Your goal',
    render: meta => <StepShell {...meta} question="Have you spoken with a mortgage broker about how much you may need?" onBack={goBack} data-id="element-271" data-mp="3g4hfc6bt13h1">
        <div className="space-y-3" data-id="element-272" data-mp="24qdld057vfbz">
          <ChoiceButton label="Yes" selected={answers.brokerStatus === 'yes'} onClick={() => pick('brokerStatus', 'yes')} data-id="element-273" data-mp="24p2l3aazcppm" />
          <ChoiceButton label="No" selected={answers.brokerStatus === 'no'} onClick={() => pick('brokerStatus', 'no')} data-id="element-274" data-mp="225foxqiay79d" />
          <ChoiceButton label="I'm not sure" selected={answers.brokerStatus === 'unsure'} onClick={() => pick('brokerStatus', 'unsure')} data-id="element-275" data-mp="1x8p64vf44xk9" />
        </div>
      </StepShell>
  });
  if (answers.brokerStatus === 'yes') {
    steps.push({
      stage: 'Your goal',
      render: meta => <StepShell {...meta} question="What savings or deposit target did your broker give you?" note="We'll show this next to our estimate so you can compare the two." onBack={goBack} onNext={goNext} data-id="element-276" data-mp="1egmw42wz8fiz">
          <MoneyInput label="Deposit or total savings target" value={answers.brokerTarget} onChange={value => set('brokerTarget', value)} onEnter={goNext} data-id="element-277" data-mp="1neyl60i7359b" />
        </StepShell>
    });
  } else if (answers.brokerStatus) {
    steps.push({
      stage: 'Your goal',
      render: meta => <StepShell {...meta} question="That's completely normal" onBack={goBack} onNext={goNext} nextLabel="Keep going" data-id="element-278" data-mp="2zseytqklixx4">
          <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 p-6" data-id="element-279" data-mp="39jjb2iiw04o9">
            <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed" data-id="element-280" data-mp="2bwj8aw1owv5d">
              {BROKER_REASSURANCE}
            </p>
            <div className="mt-6" data-id="element-281" data-mp="2gwovo00xfvhn">
              <BookCallLink data-id="element-282" data-mp="2lt5r7faghc4r" />
            </div>
          </div>
        </StepShell>
    });
  }

  // ---- Property ----------------------------------------------------------

  steps.push({
    stage: 'Property',
    render: meta => <StepShell {...meta} question="What property price are you aiming for?" note="A rough figure is fine — you can always change it later." onBack={goBack} onNext={goNext} data-id="element-283" data-mp="12wtnjca2xwz">
        <MoneyInput label="Target property price" value={answers.propertyPrice} onChange={value => set('propertyPrice', value)} onEnter={goNext} data-id="element-284" data-mp="1pox0lz6mj1in" />
      </StepShell>
  });
  steps.push({
    stage: 'Property',
    render: meta => <StepShell {...meta} question="When would you like to purchase?" onBack={goBack} data-id="element-285" data-mp="1btl71v47fh3b">
        <div className="space-y-3" data-id="element-286" data-mp="11xhstqw2vs00">
          {TIMEFRAME_OPTIONS.map(option => <ChoiceButton key={option.value} label={option.label} selected={answers.timeframe === option.value} onClick={() => pick('timeframe', option.value)} data-id="element-287" data-mp="11zm4ww44rxbd" />)}
        </div>
      </StepShell>
  });
  steps.push({
    stage: 'Property',
    render: meta => <StepShell {...meta} question="Are you buying by yourself or with someone else?" onBack={goBack} data-id="element-288" data-mp="2nakxsmawlnv8">
        <div className="space-y-3" data-id="element-289" data-mp="1w6a5ouu5xljl">
          <ChoiceButton label="By myself" selected={answers.buyingWith === 'alone'} onClick={() => pick('buyingWith', 'alone')} data-id="element-290" data-mp="214bqbk78lsx6" />
          <ChoiceButton label="With someone else" description="A partner, family member or friend." selected={answers.buyingWith === 'together'} onClick={() => pick('buyingWith', 'together')} data-id="element-291" data-mp="1mdkv026fsbpb" />
        </div>
      </StepShell>
  });

  // ---- Savings target ----------------------------------------------------

  steps.push({
    stage: 'Savings target',
    render: meta => <StepShell {...meta} question="Now let's work out your real target" onBack={goBack} onNext={goNext} data-id="element-292" data-mp="18f4ghp2c1gwm">
        <div className="rounded-2xl border border-primary-500/30 bg-primary-50 dark:bg-primary-500/10 p-6" data-id="element-293" data-mp="2xsrfvgack2aa">
          <p className="text-base text-slate-700 dark:text-slate-200 leading-relaxed" data-id="element-294" data-mp="32rkuxd5a81ck">
            {SAVINGS_GOAL_EXPLAINER}
          </p>
        </div>
      </StepShell>
  });
  steps.push({
    stage: 'Savings target',
    render: meta => <StepShell {...meta} question="How much deposit are you planning to put in?" note="If you're not sure yet, put in what you're aiming for." onBack={goBack} onNext={goNext} data-id="element-295" data-mp="10xgdcipi6pl9">
        <MoneyInput label="Deposit amount" value={answers.deposit} onChange={value => set('deposit', value)} onEnter={goNext} data-id="element-296" data-mp="19vi9ajudqs1o" />
        <MinimumDepositNote propertyPrice={answers.propertyPrice} deposit={answers.deposit} isInvestment={isInvestor} data-id="element-297" data-mp="1ju7tij9r0ol3" />
      </StepShell>
  });
  steps.push({
    stage: 'Savings target',
    render: meta => <StepShell {...meta} question="What do you expect the purchasing costs to be?" note="Things like government charges, legal fees and inspections. This may vary depending on purpose and if any stamp duty concessions are available to you." onBack={goBack} onNext={goNext} data-id="element-298" data-mp="3gnj2hmt6z2o9">
        {/* The one deliberate exception to one-question-per-screen: this toggle
            and the amount are the same decision, so splitting them would only
            add a tap. Investors never see it — no concession can apply. */}
        {!isInvestor && <div className="mb-5" data-id="element-299" data-mp="3ve44wco5dktx">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2.5" data-id="element-300" data-mp="3qcn74q0ymehk">
              Are you a first home buyer?
            </p>
            <div className="flex gap-3" data-id="element-301" data-mp="3lkvor2ryjf2i">
              {[{
            label: 'Yes',
            value: true
          }, {
            label: 'No',
            value: false
          }].map(option => <button key={option.label} type="button" aria-pressed={answers.isFirstHomeBuyer === option.value} onClick={() => set('isFirstHomeBuyer', option.value)} className={`flex-1 px-5 py-3.5 rounded-xl border text-base font-semibold transition-colors ${answers.isFirstHomeBuyer === option.value ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-300' : 'border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:border-primary-400'}`} data-id="element-302" data-mp="d53kyiimz3ff">
                  {option.label}
                </button>)}
            </div>
            <p className="mt-2.5 text-xs text-slate-400 dark:text-slate-500 leading-relaxed" data-id="element-303" data-mp="1i4rzjz0jkyz7">
              Concessions usually require never having owned property anywhere —
              so an investment property in your name generally rules it out.
            </p>
          </div>}

        {(isInvestor || answers.isFirstHomeBuyer !== null) && <StampDutyPanel propertyPrice={answers.propertyPrice} mode={!isInvestor && answers.isFirstHomeBuyer ? 'first-home' : 'investment'} data-id="element-304" data-mp="xhaczsq709ia" />}

        <div className="mt-6" data-id="element-305" data-mp="17cltafh91os5">
          <MoneyInput label="Estimated purchasing costs" value={answers.purchasingCosts} onChange={value => {
          set('purchasingCosts', value);
          set('purchasingCostsEstimated', false);
        }} onEnter={goNext} autoFocus={false} data-id="element-306" data-mp="1l5tmgbkzasby" />
        </div>
        <EstimateButton applied={answers.purchasingCostsEstimated} unavailableReason={answers.propertyPrice > 0 ? undefined : 'Add a target property price on the earlier screen and we can estimate this for you.'} onApply={() => {
        set('purchasingCosts', estimatePurchasingCosts(answers.propertyPrice));
        set('purchasingCostsEstimated', true);
      }} data-id="element-307" data-mp="1q5ppa7hxwdpx" />
      </StepShell>
  });
  steps.push({
    stage: 'Savings target',
    render: meta => <StepShell {...meta} question="How much cash do you want left over after buying?" note="Your breathing room once the purchase is done. From a one month mortgage repayment to what you feel comfortable with." onBack={goBack} onNext={goNext} data-id="element-308" data-mp="22lxrmfydidbl">
        <MoneyInput label="Cash buffer" value={answers.cashBuffer} onChange={value => {
        set('cashBuffer', value);
        set('cashBufferEstimated', false);
      }} onEnter={goNext} data-id="element-309" data-mp="1ereaywzf3jvy" />
        <EstimateButton applied={answers.cashBufferEstimated} onApply={() => {
        set('cashBuffer', estimateCashBuffer(answers.livingExpenses));
        set('cashBufferEstimated', true);
      }} data-id="element-310" data-mp="3e3h12q2jrcil" />
      </StepShell>
  });
  if (isInvestor) {
    steps.push({
      stage: 'Savings target',
      render: meta => <StepShell {...meta} question="How much do you want to hold back as an investor reserve?" note="Money set aside for vacancies, repairs and rate rises." onBack={goBack} onNext={goNext} data-id="element-311" data-mp="151ue3zsa43a6">
          <MoneyInput label="Investor reserve" value={answers.investorReserve} onChange={value => set('investorReserve', value)} onEnter={goNext} data-id="element-312" data-mp="qa28wgm785m6" />
        </StepShell>
    });
  }
  steps.push({
    stage: 'Savings target',
    render: meta => <StepShell {...meta} question="Any other costs you're expecting?" note="Moving, furniture, renovations. Leave it at zero if there's nothing." onBack={goBack} onNext={goNext} data-id="element-313" data-mp="ca110h7inxx7">
        <MoneyInput label="Other expected costs" value={answers.otherCosts} onChange={value => set('otherCosts', value)} onEnter={goNext} data-id="element-314" data-mp="2j1p857pg18vr" />
      </StepShell>
  });
  steps.push({
    stage: 'Savings target',
    render: meta => <RevealScreen {...meta} eyebrow="Your total savings goal" headlineValue={results.totalSavingsGoal} breakdown={[{
      label: 'Deposit',
      value: answers.deposit
    }, {
      label: 'Purchasing costs',
      value: answers.purchasingCosts,
      estimated: answers.purchasingCostsEstimated
    }, {
      label: 'Cash buffer',
      value: answers.cashBuffer,
      estimated: answers.cashBufferEstimated
    }, ...(isInvestor ? [{
      label: 'Investor reserve',
      value: answers.investorReserve
    }] : []), {
      label: 'Other costs',
      value: answers.otherCosts
    }]} note="This is the number that actually gets you to settlement — not just the deposit." onBack={goBack} onNext={goNext} data-id="element-315" data-mp="2wykoll80y2lu" />
  });

  // ---- What you have -----------------------------------------------------

  steps.push({
    stage: 'What you have',
    render: meta => <StepShell {...meta} question="How much have you saved so far?" note="Just the money you've actually got set aside for this." onBack={goBack} onNext={goNext} data-id="element-316" data-mp="20yqebtbz9nn4">
        <MoneyInput label="Amount saved" value={answers.savedSoFar} onChange={value => set('savedSoFar', value)} onEnter={goNext} data-id="element-317" data-mp="2au1w6kionaz3" />
      </StepShell>
  });
  steps.push({
    stage: 'What you have',
    render: meta => <StepShell {...meta} question="Are you receiving a confirmed gift or contribution?" note="Only count it if it's genuinely confirmed. Leave it at zero if not." onBack={goBack} onNext={goNext} data-id="element-318" data-mp="3tt88j5vv3u4d">
        <MoneyInput label="Gift or contribution" value={answers.giftAmount} onChange={value => set('giftAmount', value)} onEnter={goNext} data-id="element-319" data-mp="3ouyamnir6ff2" />
      </StepShell>
  });
  steps.push({
    stage: 'What you have',
    render: meta => <StepShell {...meta} question="Any other confirmed funds?" note="Shares you're selling, a maturing term deposit, anything already locked in." onBack={goBack} onNext={goNext} data-id="element-320" data-mp="lg2acfpvfnwv">
        <MoneyInput label="Other confirmed funds" value={answers.otherConfirmedFunds} onChange={value => set('otherConfirmedFunds', value)} onEnter={goNext} data-id="element-321" data-mp="zdh41wn5qhun" />
      </StepShell>
  });
  steps.push({
    stage: 'What you have',
    render: meta => <StepShell {...meta} question="How much do you want to keep untouched?" note="Savings you don't want to put toward the purchase. We'll leave this out of the total." onBack={goBack} onNext={goNext} data-id="element-322" data-mp="2vglx1gpx166s">
        <MoneyInput label="Amount kept untouched" value={answers.keepUntouched} onChange={value => set('keepUntouched', value)} onEnter={goNext} data-id="element-323" data-mp="2meckklb372qr" />
      </StepShell>
  });
  steps.push({
    stage: 'What you have',
    render: meta => <RevealScreen {...meta} eyebrow={results.goalReached ? "You're already there" : 'Still to save'} headlineValue={results.savingsGap} subValue={{
      label: 'Available to put in right now:',
      value: results.usableSavings
    }} progress={results.progress} breakdown={[{
      label: 'Total savings goal',
      value: results.totalSavingsGoal
    }, {
      label: 'Usable savings',
      value: results.usableSavings
    }]} note={results.goalReached ? "You've got the funds together. The next step is confirming the numbers against a real property." : 'Next, a quick look at your income so we can see whether this goal is realistic.'} onBack={goBack} onNext={goNext} data-id="element-324" data-mp="3a46y7538cvc2" />
  });

  // ---- Income and commitments --------------------------------------------

  steps.push({
    stage: 'Income',
    render: meta => <StepShell {...meta} question="A quick look at your income" onBack={goBack} onNext={goNext} data-id="element-325" data-mp="2hbbq5l3p5u5v">
        <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 p-6" data-id="element-326" data-mp="hzssi2nug8bi">
          <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed" data-id="element-327" data-mp="my40s8yhqarm">
            {INCOME_EXPLAINER}
          </p>
          <div className="mt-5 pt-5 border-t border-slate-200 dark:border-white/10 flex gap-3" data-id="element-328" data-mp="2v37jpwp584ux">
            <ShieldCheckIcon className="w-5 h-5 flex-shrink-0 text-primary-500 mt-0.5" data-id="element-329" data-mp="302apjjcy6f3y" />
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed" data-id="element-330" data-mp="300hgxyirxktb">
              {PRIVACY_NOTE}
            </p>
          </div>
        </div>
      </StepShell>
  });
  const incomeSteps = (which: 'applicantOne' | 'applicantTwo', who: string): StepDef[] => {
    const applicant = answers[which];
    const prefix = isJoint ? `${who} — ` : '';
    return [{
      stage: 'Income',
      render: meta => <StepShell {...meta} question={`${prefix}What's your employment type?`} onBack={goBack} data-id="element-331" data-mp="3nqty7zzwstwk">
            <div className="space-y-3" data-id="element-332" data-mp="1oh5c559ftntm">
              {EMPLOYMENT_OPTIONS.map(option => <ChoiceButton key={option.value} label={option.label} selected={applicant.employmentType === option.value} onClick={() => {
            setApplicant(which, {
              employmentType: option.value
            });
            goNext();
          }} data-id="element-333" data-mp="1tfoyv7vuv3n1" />)}
            </div>
          </StepShell>
    }, {
      stage: 'Income',
      render: meta => <StepShell {...meta} question={`${prefix}What's your income before tax?`} note="Your yearly salary or wage, before tax comes out." onBack={goBack} onNext={goNext} data-id="element-334" data-mp="1ztm6b3oiitkk">
            <MoneyInput label="Gross annual income" suffix="a year" value={applicant.grossAnnualIncome} onChange={value => setApplicant(which, {
          grossAnnualIncome: value
        })} onEnter={goNext} data-id="element-335" data-mp="1qt637vbolog8" />
          </StepShell>
    }, {
      stage: 'Income',
      render: meta => <StepShell {...meta} question={`${prefix}Any regular overtime, bonuses or allowances?`} note="Only what you receive reliably. Leave it at zero if it varies a lot." onBack={goBack} onNext={goNext} data-id="element-336" data-mp="6xiekyn3vioo">
            <MoneyInput label="Overtime, bonuses or allowances" suffix="a year" value={applicant.extraIncome} onChange={value => setApplicant(which, {
          extraIncome: value
        })} onEnter={goNext} data-id="element-337" data-mp="3u8hrr9un7gme" />
          </StepShell>
    }, {
      stage: 'Income',
      render: meta => <StepShell {...meta} question={`${prefix}Any rental or other income?`} note="Rent from another property, a side business, government payments." onBack={goBack} onNext={goNext} data-id="element-338" data-mp="3fc0h6kua260w">
            <MoneyInput label="Rental or other income" suffix="a year" value={applicant.rentalOtherIncome} onChange={value => setApplicant(which, {
          rentalOtherIncome: value
        })} onEnter={goNext} data-id="element-339" data-mp="35pg22k4217q0" />
          </StepShell>
    }];
  };
  steps.push(...incomeSteps('applicantOne', 'You'));
  if (isJoint) {
    steps.push(...incomeSteps('applicantTwo', 'Your co-buyer'));
  }
  steps.push({
    stage: 'Income',
    render: meta => <StepShell {...meta} question="How many dependants do you have?" note="Children or anyone else who relies on your income." onBack={goBack} onNext={goNext} data-id="element-340" data-mp="3kgq40mp21qwl">
        <NumberStepper label="Dependants" value={answers.dependants} onChange={value => set('dependants', value)} data-id="element-341" data-mp="hbqw5okt6kux" />
      </StepShell>
  });
  steps.push({
    stage: 'Income',
    render: meta => <StepShell {...meta} question="What do you currently owe?" note="Open only the ones that apply to you." onBack={goBack} onNext={goNext} data-id="element-342" data-mp="q5dnn2hzh1yb">
        <DebtsGroup debts={answers.debts} onChange={setDebts} data-id="element-343" data-mp="3ujc5kklrqe5a" />
        <p className="mt-6 text-sm text-slate-500 dark:text-slate-400 leading-relaxed" data-id="element-344" data-mp="2e8aiv3fgnrp">
          {COMMITMENTS_NOTE}
        </p>
      </StepShell>
  });
  steps.push({
    stage: 'Income',
    render: meta => <StepShell {...meta} question="How much rent are you paying now?" note="Leave it at zero if you're not renting." onBack={goBack} onNext={goNext} data-id="element-345" data-mp="ccxuctevwial">
        <MoneyInput label="Current rent" suffix="a month" value={answers.currentRent} onChange={value => set('currentRent', value)} onEnter={goNext} data-id="element-346" data-mp="1nr1n5w9qk0e2" />
      </StepShell>
  });
  steps.push({
    stage: 'Income',
    render: meta => <StepShell {...meta} question="Roughly what do you spend each month to live?" note="Groceries, bills, transport, insurance, the fun stuff. A ballpark is fine." onBack={goBack} onNext={goNext} data-id="element-347" data-mp="2yjaeclzrbnvk">
        <MoneyInput label="Monthly living expenses" suffix="a month" value={answers.livingExpenses} onChange={value => set('livingExpenses', value)} onEnter={goNext} data-id="element-348" data-mp="2phawxsktyhs3" />
      </StepShell>
  });

  // ---- Your plan ---------------------------------------------------------

  steps.push({
    stage: 'Your plan',
    render: meta => <StepShell {...meta} question="How often are you paid?" onBack={goBack} data-id="element-349" data-mp="drqr1u4yy2fo">
        <div className="space-y-3" data-id="element-350" data-mp="8v9qu1khvxm8">
          {PAY_FREQUENCY_OPTIONS.map(option => <ChoiceButton key={option.value} label={option.label} selected={answers.payFrequency === option.value} onClick={() => pick('payFrequency', option.value)} data-id="element-351" data-mp="23a6vml654ir0" />)}
        </div>
      </StepShell>
  });
  steps.push({
    stage: 'Your plan',
    render: meta => <StepShell {...meta} question="How much are you saving each pay right now?" onBack={goBack} onNext={goNext} data-id="element-352" data-mp="2m1pwalcozkpv">
        <MoneyInput label="Currently saving each pay" suffix="each pay" value={answers.savingNowPerPay} onChange={value => set('savingNowPerPay', value)} onEnter={goNext} data-id="element-353" data-mp="2r018h2qgm7b9" />
      </StepShell>
  });
  steps.push({
    stage: 'Your plan',
    render: meta => <StepShell {...meta} question="How much could you comfortably save each pay?" note="Be honest rather than optimistic — this is the number your plan is built on." onBack={goBack} onNext={goNext} nextLabel="See my roadmap" data-id="element-354" data-mp="11nzdrtyh4l96">
        <MoneyInput label="Could comfortably save each pay" suffix="each pay" value={answers.couldSavePerPay} onChange={value => set('couldSavePerPay', value)} onEnter={goNext} data-id="element-355" data-mp="t4cg8iu8ig0j" />
        {answers.savingNowPerPay > 0 && answers.couldSavePerPay > answers.savingNowPerPay && <p className="mt-4 flex items-start gap-2 text-sm text-primary-700 dark:text-primary-300" data-id="element-356" data-mp="aemo63huw8cb">
              <ArrowRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-357" data-mp="p393443kmy30" />
              That's more than you're saving now — worth setting up as an
              automatic transfer.
            </p>}
      </StepShell>
  });
  stepCount = steps.length;
  const safeIndex = Math.min(index, stepCount - 1);
  const current = steps[safeIndex];
  return <>
      {current.render({
      step: safeIndex + 1,
      total: stepCount,
      stage: current.stage
    })}
    </>;
}